import { Controller } from "@hotwired/stimulus"

// Screenshot picker for the help-article editor (A2 in the delivery pipeline).
//
// Lists committed screenshots under public/help-screenshots/ via the help API
// (scoped to the article's slug first), shows them as a thumbnail grid in a
// dropdown, and inserts the chosen image into the Lexxy editor at the cursor —
// so an author clicks a thumbnail instead of typing a path. Native Lexxy
// drag-drop still works for ad-hoc human images.
//
// Markup contract:
//   data-controller="screenshot-picker"
//   data-screenshot-picker-endpoint-value="/api/v1/help/screenshots"
//   data-screenshot-picker-slug-value="<article-slug>"   (optional)
//   data-screenshot-picker-editor-value="#article_content" (editor selector; optional)
//   [data-screenshot-picker-target="trigger"]  button to open
//   [data-screenshot-picker-target="dropdown"] hidden container
//   [data-screenshot-picker-target="grid"]     where thumbnails render
//   [data-screenshot-picker-target="empty"]    shown when no screenshots
interface Screenshot {
  name: string
  url: string
  slug: string | null
}

export default class extends Controller<HTMLElement> {
  static targets = ["dropdown", "grid", "empty"]
  static values = {
    endpoint: { type: String, default: "/api/v1/help/screenshots" },
    slug: { type: String, default: "" },
    editor: { type: String, default: "lexxy-editor" },
    loaded: { type: Boolean, default: false },
  }

  declare readonly dropdownTarget: HTMLElement
  declare readonly gridTarget: HTMLElement
  declare readonly hasEmptyTarget: boolean
  declare readonly emptyTarget: HTMLElement
  declare endpointValue: string
  declare slugValue: string
  declare editorValue: string
  declare loadedValue: boolean

  // Close when clicking outside.
  close = (event: Event) => {
    if (!this.element.contains(event.target as Node)) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  connect() {
    document.addEventListener("click", this.close)
  }

  disconnect() {
    document.removeEventListener("click", this.close)
  }

  async toggle(event: Event) {
    event.preventDefault()
    const willOpen = this.dropdownTarget.classList.contains("hidden")
    this.dropdownTarget.classList.toggle("hidden")
    if (willOpen && !this.loadedValue) {
      await this.load()
    }
  }

  async load() {
    const url = new URL(this.endpointValue, window.location.origin)
    if (this.slugValue) url.searchParams.set("slug", this.slugValue)

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.render(data.screenshots || [])
      this.loadedValue = true
    } catch (error) {
      this.render([])
    }
  }

  render(screenshots: Screenshot[]) {
    this.gridTarget.innerHTML = ""

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", screenshots.length > 0)
    }

    for (const shot of screenshots) {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "screenshot-thumb"
      button.dataset.url = shot.url
      button.dataset.name = shot.name
      button.title = shot.name
      button.addEventListener("click", (event) => this.insert(event))

      const img = document.createElement("img")
      img.src = shot.url
      img.alt = shot.name
      img.loading = "lazy"
      button.appendChild(img)

      const caption = document.createElement("span")
      caption.className = "screenshot-thumb-caption"
      caption.textContent = shot.name
      button.appendChild(caption)

      this.gridTarget.appendChild(button)
    }
  }

  insert(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLElement
    const url = button.dataset.url
    const name = button.dataset.name || ""
    if (!url) return

    const editorElement = document.querySelector(this.editorValue) as
      | (HTMLElement & { contents?: { insertHtml(html: string): void } })
      | null
    if (!editorElement?.contents) return

    const image = document.createElement("img")
    image.src = url
    image.alt = this.altFromName(name)
    editorElement.contents.insertHtml(image.outerHTML)

    this.dropdownTarget.classList.add("hidden")
    this.dispatch("inserted", { detail: { url, name } })
  }

  // "case-notes-form.png" -> "case notes form"
  private altFromName(name: string): string {
    return name
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[-_]/g, " ")
      .trim()
  }
}
